import { useCallback, useEffect, useRef } from 'react';

/**
 * Ao trocar de página, leva a tela e o foco ao início dos resultados: a página trocava os
 * cartões e continuava lá embaixo, na paginação. O foco vai junto para o teclado seguir do primeiro
 * cartão, sem rolar de novo. O contêiner do início dos resultados usa `scroll-mt-[140px]`: deixa à vista, acima
 * dos cartões, o título da seção e a faixa fixa do ambiente de homologação.
 *
 * A rolagem acontece duas vezes: no clique, para sair de baixo na hora, e quando a página nova termina
 * de carregar. Durante a carga a lista fica vazia, a página encolhe, e o navegador não consegue rolar até
 * o início; com os cartões de volta, a segunda rolagem acerta a posição. A segunda só vale para a carga
 * que a troca de página abriu: o hook espera ver `carregando` ligar e desligar depois do clique, e não
 * confunde com o fim de uma carga anterior que termine logo depois dele.
 */
export function useInicioDosResultados<T extends HTMLElement = HTMLDivElement>(carregando: boolean) {
  const ref = useRef<T>(null);
  // `aguardando`: houve troca de página; `carregou`: a carga dela já começou.
  const etapa = useRef<'parado' | 'aguardando' | 'carregou'>('parado');

  const rolar = useCallback(() => {
    const inicio = ref.current;
    if (!inicio) return;

    // Salto direto, sem `smooth`: a página muda de altura no meio da animação, que parava antes dos cartões.
    inicio.scrollIntoView({ block: 'start' });
    inicio.focus({ preventScroll: true });
  }, []);

  const irParaOInicio = useCallback(() => {
    etapa.current = 'aguardando';
    rolar();
  }, [rolar]);

  useEffect(() => {
    if (carregando && etapa.current === 'aguardando') etapa.current = 'carregou';
    if (!carregando && etapa.current === 'carregou') {
      etapa.current = 'parado';
      rolar();
    }
  }, [carregando, rolar]);

  return { ref, irParaOInicio };
}
